// noinspection JSUnusedGlobalSymbols

import { omit, propEq, assocPath, pipe } from 'ramda'
import FormData from 'form-data'
import { ls } from '@iqueue/ui-kit'
import { stringify as qsStringify } from 'qs'
import { getServerAddress } from '../utils/system'
import moment from 'moment'

function isFormData(data) {
  if (data instanceof URLSearchParams) return true
  if (typeof window !== 'undefined' && data instanceof window.FormData) return true
  return data instanceof FormData
}

function qs(params) {
  return qsStringify(params, {
    addQueryPrefix: true,
    allowDots: true,
  })
}

function trimUrl(url) {
  return url.replace(/^\/+|\/+$/g, '')
}

export class HTTPError extends Error {
  constructor(message, status = 400) {
    super(message)
    this.status = status
  }
}

async function fetcher(url = '', { query = {}, ...options }) {
  let error, status

  try {
    const res = await fetch(trimUrl(getServerAddress() + '/api/' + url) + qs(query),
      pipe(
        assocPath(['headers', 'Authorization'], `Bearer ${ ls('amber-auth-token') || '' }`),
      )(options),
    )

    status = res.status

    if (propEq('HEAD', 'method', options) && res.status < 400) {
      return true
    }

    if (res.status < 400) {
      const headers = {}

      for (const [key, value] of res.headers.entries()) {
        headers[key.toLowerCase()] = value
      }

      if (headers['x-license-expiration']) {
        const event = new Event("ab-license")
        event.data = {
          expiration: moment(Number(headers['x-license-expiration']) * 1000),
          information: headers['x-license-information'],
        }
        document.dispatchEvent(event)
      }

      return {
        headers,
        data: headers['content-type']?.includes('application/json') ? await res.json() : await res.text(),
      }
    }

    if ([404, 500].includes(res.status)) {
      error = res.statusText
    } else {
      const json = await res.json()
      error = json ? JSON.stringify(json?.message) : 'Network error'
    }
  } catch (e) {
    console.warn(e)
    error = e.message
  }

  if (error) throw new HTTPError(error, status)
}

export async function listenSSE(urn, callback) {
  const url = `${ getServerAddress() }/api/v1/sensors/${ urn }`

  const eventSource = new EventSource(`${ url }?token=${ encodeURIComponent(ls('amber-auth-token')) }`)

  eventSource.onmessage = (event) => {

    let data

    try {
      data = JSON.parse(event.data)
    } catch (ignored) {
      data = event.data
    }

    const result = callback(data, event)

    if (result?.cancel || data?.complete) {
      eventSource.close()
    }
  }

  return () => {
    eventSource.onerror = (error) => {
      console.error('Connection error:', error)
      if (error.status === 204) {
        eventSource.close()
      }
    }
  }
}

export async function fetcherPaginated(url, options = {}) {
  const { data, headers } = (await fetcher(url, {
    method: 'GET',
    redirect: 'follow',
    ...options,
  }))

  return {
    entries: data,
    total: +headers['x-pagination-total'],
    pages: +headers['x-pagination-pages'],
    page: +headers['x-pagination-page'],
    limit: +headers['x-pagination-limit'],
  }
}

export async function fetcherGet(url, options = {}) {
  return (await fetcher(url, {
    method: 'GET',
    redirect: 'follow',
    ...options,
  })).data
}

export async function fetcherPost(url, data, options = {}, appendHeaders = true) {
  const isFormDataObject = isFormData(data)
  const headers = appendHeaders ? {
    'Content-Type': isFormDataObject ? 'application/x-www-form-urlencoded' : 'application/json',
    ...(options.headers || {}),
  } : options.headers || {}

  return fetcher(url, {
    method: 'POST',
    headers,
    redirect: 'follow',
    body: isFormDataObject ? data : JSON.stringify(data),
    ...omit(['headers'], options),
  })
}

export async function fetcherPatch(url, data, options = {}) {
  const isFormDataObject = isFormData(data) // Проверяем, является ли тело FormData

  return fetcher(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': isFormDataObject ? 'application/x-www-form-urlencoded' : 'application/json',
      ...(options.headers || {}),
    },
    redirect: 'follow',
    body: isFormDataObject ? data : JSON.stringify(data),
    ...omit(['headers'], options),
  })
}

export async function fetcherPut(url, data, options = {}) {
  const isFormDataObject = isFormData(data)

  return fetcher(url, {
    method: 'PUT',
    headers: {
      'Content-Type': isFormDataObject ? 'application/x-www-form-urlencoded' : 'application/json',
      ...(options.headers || {}),
    },
    redirect: 'follow',
    body: isFormDataObject ? data : JSON.stringify(data),
    ...omit(['headers'], options),
  })
}

export async function fetcherHead(url) {
  return fetcher(url, {
    method: 'HEAD',
    redirect: 'follow',
  })
}

export async function fetcherDelete(url) {
  return fetcher(url, {
    method: 'DELETE',
    redirect: 'follow',
  })
}
